/* eslint-disable jsx-a11y/click-events-have-key-events */
import "./Step.css";

import { Dispatch, forwardRef, SetStateAction } from "react";

import {
  CircleFilledIcon,
  CompletedCircleGreenIcon,
  ErrorIcon,
  MinusIcon,
  PlusIcon,
  RingIcon,
  VerticalLineLightIcon,
} from "../../../../../assets";
import { ColorConstants, OrganisationTypeConstants } from "../../../../../constants";
import { StepState } from "../../../../../models";
import { TabNotificationIndicator } from "../../../../wayfinding/Tabs/components";
import { SubStep, useStep } from "./useStep";

export interface StepProps {
  key: string;
  stepHeader: string;
  current: boolean;
  // only applies to L1 steps with no children; `undefined` otherwise.
  valid?: boolean | null;
  subSteps: SubStep[];
}

interface StepComponentProps {
  step: StepProps;
  lastStep: boolean;
  isReview?: boolean;
  currentUserType: OrganisationTypeConstants;
  handleMoveToStep?: (stepKeys: string[]) => void;
  clickedSubStep: string[] | undefined;
  setClickedSubStep: Dispatch<SetStateAction<string[] | undefined>>;
}

export const Step = forwardRef(
  (
    {
      step,
      lastStep,
      isReview,
      currentUserType,
      handleMoveToStep,
      clickedSubStep,
      setClickedSubStep,
    }: StepComponentProps,
    forwardedRef
  ): JSX.Element => {
    const { showSubSteps, setShowSubSteps, stepState, onStepSelect, onSubStepSelect } = useStep(
      { step, handleMoveToStep, isReview, clickedSubStep, setClickedSubStep },
      forwardedRef
    );

    const hasSubSteps = !!step.subSteps.length;

    let stepColours = [] as string[];
    switch (currentUserType) {
      case OrganisationTypeConstants.ASSET_MANAGER:
        stepColours = [
          ColorConstants.GREEN_300,
          ColorConstants.GREY_400,
          ColorConstants.INDIGO_500,
          ColorConstants.INDIGO_500,
          ColorConstants.INDIGO_500,
        ];
        break;
      case OrganisationTypeConstants.VERIFIER:
        stepColours = [
          ColorConstants.WHITE,
          ColorConstants.WHITE,
          ColorConstants.GREEN_400,
          ColorConstants.GREEN_500,
          ColorConstants.GOLDENROD_600,
          ColorConstants.RED_300,
        ];
        break;
      case OrganisationTypeConstants.DEVELOPER:
        stepColours = [
          ColorConstants.WHITE,
          ColorConstants.WHITE,
          ColorConstants.GREEN_200,
          ColorConstants.GREEN_300,
          ColorConstants.RED_300,
          ColorConstants.GOLDENROD_600,
        ];
        break;
      case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
        stepColours = [
          ColorConstants.GREEN_300,
          ColorConstants.GREY_400,
          ColorConstants.INDIGO_500,
          ColorConstants.INDIGO_500,
          ColorConstants.INDIGO_500,
        ];
        break;
      default:
        stepColours = [
          ColorConstants.GREEN_100,
          ColorConstants.GREY_500,
          ColorConstants.GREEN_400,
          ColorConstants.GREEN_500,
          ColorConstants.RED_500,
        ];
    }

    const getSubStepColor = (subStep: SubStep): string | undefined => {
      let color = stepColours[0];

      if (subStep.valid) {
        color = ColorConstants.GREEN_500;
      }

      if (subStep.valid === false) {
        color =
          currentUserType === OrganisationTypeConstants.VERIFIER
            ? ColorConstants.GOLDENROD_600
            : ColorConstants.RED_500;
      }
      // Special case for verifier and developer
      if (subStep.valid === null) {
        color =
          currentUserType === OrganisationTypeConstants.VERIFIER
            ? ColorConstants.RED_300
            : ColorConstants.GOLDENROD_600;
      }

      return color;
    };

    const getStepLabelColor = (subStep: SubStep): string | undefined => {
      let color = stepColours[1];

      if (!isReview && subStep.current) {
        // eslint-disable-next-line prefer-destructuring
        color = stepColours[2];
        if (subStep.valid) {
          // eslint-disable-next-line prefer-destructuring
          color = stepColours[3];
        }

        if (subStep.valid === false) {
          // eslint-disable-next-line prefer-destructuring
          color = stepColours[4];
        }

        // Special case for verifier and developer
        if (subStep.valid === null && stepColours[5] !== undefined) {
          // eslint-disable-next-line prefer-destructuring
          color = stepColours[5];
        }
      }
      return color;
    };

    const getHeaderStepIcon = (): JSX.Element => {
      switch (stepState) {
        case StepState.NotStarted:
          return <RingIcon fill={stepColours[0]} />;
        case StepState.InProgress:
          return <RingIcon fill={ColorConstants.GREEN_500} />;
        case StepState.Complete:
          return <CompletedCircleGreenIcon />;
        case StepState.Invalid:
          return currentUserType === OrganisationTypeConstants.VERIFIER ? (
            <RingIcon fill={ColorConstants.GOLDENROD_600} />
          ) : (
            <ErrorIcon className="ErrorIcon" width={24} height={24} />
          );
        // Special case for verifier and developer
        case StepState.Warning:
          return currentUserType === OrganisationTypeConstants.DEVELOPER ? (
            <RingIcon fill={ColorConstants.GOLDENROD_600} />
          ) : (
            <ErrorIcon className="ErrorIcon" width={24} height={24} />
          );
        default:
          return <RingIcon fill={stepColours[0]} />;
      }
    };

    // TODO: add unread message logic
    const stepHasUnreadMessages = false;

    return (
      <div className={`${currentUserType} Step`}>
        <div
          role="button"
          onKeyDown={(e) => (e.key === "Enter" && hasSubSteps ? setShowSubSteps(!showSubSteps) : onStepSelect())}
          onClick={() => (hasSubSteps ? setShowSubSteps(!showSubSteps) : onStepSelect())}
          tabIndex={0}
          aria-label={hasSubSteps ? "Show Substeps" : "Select Step"}
          className="Header"
        >
          <div className="HeaderLeftSection">
            {getHeaderStepIcon()}
            <div>
              <p className={`${step.current && !isReview ? "Current" : ""} body2`}>{step.stepHeader}</p>
              {stepHasUnreadMessages && <TabNotificationIndicator />}
            </div>
          </div>
          {hasSubSteps && <div className="ExpandOrContractButton">{showSubSteps ? <MinusIcon /> : <PlusIcon />}</div>}
        </div>
        {hasSubSteps && showSubSteps && (
          <div className={`SubSteps ${lastStep && "LastStep"}`}>
            {step.subSteps.map((subStep, index) => {
              // TODO: add unread message logic
              const subStepHasUnreadMessages = false;
              return index !== 0 ? (
                <div className={`SubStep${subStep.current && !isReview ? " Current" : ""}`} key={subStep.key}>
                  <VerticalLineLightIcon className="VerticalLineLightIcon" stroke={getSubStepColor(subStep)} />
                  <div
                    role="button"
                    onKeyDown={(e) => (e.key === "Enter" ? onSubStepSelect(subStep.key) : null)}
                    onClick={() => onSubStepSelect(subStep.key)}
                    className="SubStepSelectable"
                    tabIndex={0}
                  >
                    <CircleFilledIcon fill={getSubStepColor(subStep)} />
                    <div
                      className="body3"
                      style={{
                        color: getStepLabelColor(subStep),
                      }}
                    >
                      <div>
                        {subStep.stepName}
                        {subStepHasUnreadMessages && <TabNotificationIndicator />}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`SubStep${subStep.current && !isReview ? " Current" : ""}`} key={subStep.stepName}>
                  <div
                    role="button"
                    onKeyDown={(e) => (e.key === "Enter" ? onSubStepSelect(subStep.key) : null)}
                    onClick={() => onSubStepSelect(subStep.key)}
                    className="SubStepSelectable"
                    tabIndex={0}
                  >
                    <CircleFilledIcon fill={getSubStepColor(subStep)} />
                    <div
                      className="body3 SubStepSelectable"
                      style={{
                        color: getStepLabelColor(subStep),
                      }}
                    >
                      <div>
                        {subStep.stepName}
                        {subStepHasUnreadMessages && <TabNotificationIndicator />}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);
